import type { QueryClient } from '@tanstack/react-query';
import {
  AuditQuestionCompletionStatus,
  type GetAuditSummaryCommandOutput,
  type QuestionResponseDetails,
} from '@amzn/gbsa-helios-typescript-client';
import type { CacheSnapshot } from './patchAuditSummaryCache';
import {
  deriveParentCompletion,
  deriveParentResult,
  isSubEntryAnswered,
  type SubQuestionTypeRef,
} from './deriveParentResult';
import {
  EMPTY_RESPONSE,
  applyMetricsDiff,
  auditSummaryPredicate,
  buildNextCacheData,
} from './patchAuditCachePlumbing';

/**
 * Optimistic cache patches for PA-04 answer CLEARS (toggle-off).
 * Sibling module to patchAuditAnswerCaches (answer saves) and
 * patchAuditFieldCaches (comment / naReason / mediaIds) — same
 * snapshot/rollback contract, same per-cache-entry walk.
 *
 * Companions to the answer patches rather than extensions of them:
 * `QuestionResponsePatch` requires a `ResponseResult`, and threading
 * a null sentinel through it would force every existing call site to
 * handle a state that can't occur on the answer path. A clear is its
 * own operation.
 *
 * The shared mutation's `onMutate` skips inputs without a
 * `responseResult`, so these patches are applied CALLER-side via the
 * draft primitive's `applyClearOptimisticPatch` (the sub-question
 * answer path is the established template for caller-side patching).
 */

export interface QuestionResponseClearPatch {
  readonly respondedBy: string;
  /** Defaults to `new Date()` at patch time. */
  readonly respondedAt?: Date;
}

/**
 * PA-04 toggle-off: clear a question's answer optimistically.
 *
 *   - main response: result REMOVED (the read side models
 *     "unanswered" as an absent field), naReason removed, status
 *     back to NOT_STARTED.
 *   - sub responses: CASCADE — every entry loses result + naReason
 *     (status NOT_STARTED), mirroring the wire payload built by
 *     `buildAnswerClearUpdate`. Comments / media survive.
 *   - metrics: completion and result-bucket counters reversed.
 */
export function snapshotAndClearAuditQuestionResponse(
  queryClient: QueryClient,
  auditId: string,
  questionId: string,
  patch: QuestionResponseClearPatch,
): CacheSnapshot[] {
  const matchingQueries = queryClient.getQueriesData<GetAuditSummaryCommandOutput>({
    predicate: auditSummaryPredicate(auditId),
  });
  const snapshots: CacheSnapshot[] = [];
  const now = patch.respondedAt ?? new Date();

  for (const [queryKey, cachedData] of matchingQueries) {
    const nextData = computeQuestionResponseClearPatch(
      cachedData,
      questionId,
      patch,
      now,
    );
    if (!nextData) continue;
    snapshots.push({ queryKey, data: cachedData });
    queryClient.setQueryData(queryKey, nextData);
  }
  return snapshots;
}

function computeQuestionResponseClearPatch(
  cachedData: GetAuditSummaryCommandOutput | undefined,
  questionId: string,
  patch: QuestionResponseClearPatch,
  now: Date,
): GetAuditSummaryCommandOutput | null {
  if (!cachedData?.questions || !cachedData.audit) return null;
  const targetIndex = cachedData.questions.findIndex(
    (q) => q.questionDetails.questionId === questionId,
  );
  if (targetIndex < 0) return null;
  const targetQuestion = cachedData.questions.at(targetIndex);
  if (!targetQuestion) return null;

  const previousMainResponse = targetQuestion.responseDetails?.mainQuestionResponse;
  const previousMainResult = previousMainResponse?.responseResult;
  const previousSubResponses = targetQuestion.responseDetails?.subQuestionResponses ?? [];

  const nextMainResponse = clearResponseForCache(
    previousMainResponse,
    patch.respondedBy,
    now,
  );
  const nextSubResponses = previousSubResponses.map((entry) => ({
    ...entry,
    response: clearResponseForCache(entry.response, patch.respondedBy, now),
  }));

  // Completion is independent from result presence: fail-dominant
  // aggregation allows an incomplete parent to already report FAIL.
  const wasCompleted =
    previousMainResponse?.responseStatus === AuditQuestionCompletionStatus.COMPLETED;

  return buildNextCacheData(
    cachedData,
    targetIndex,
    nextMainResponse,
    nextSubResponses,
    applyMetricsDiff(
      cachedData.audit.metrics,
      previousMainResult,
      undefined,
      wasCompleted,
      false,
    ),
  );
}

export interface SubQuestionResponseClearPatch {
  readonly subQuestionId: string;
  readonly respondedBy: string;
  readonly respondedAt?: Date;
  /** Child id + responseType list from the full-question fetch — the
   *  summary cache only carries answered children, and parent
   *  derivation needs each child's ERT type to split scored from
   *  data-collection subs. */
  readonly subQuestionRefs: readonly SubQuestionTypeRef[];
}

/**
 * PA-04 toggle-off for ONE sub-question: drop the target's result +
 * naReason from the cache, then re-derive the parent. Completion is
 * lost because the target is unanswered, while a FAIL aggregate is
 * preserved when another answered sibling still fails. Other siblings
 * are untouched.
 */
export function snapshotAndClearAuditSubQuestionResponse(
  queryClient: QueryClient,
  auditId: string,
  questionId: string,
  patch: SubQuestionResponseClearPatch,
): CacheSnapshot[] {
  const matchingQueries = queryClient.getQueriesData<GetAuditSummaryCommandOutput>({
    predicate: auditSummaryPredicate(auditId),
  });
  const snapshots: CacheSnapshot[] = [];
  const now = patch.respondedAt ?? new Date();

  for (const [queryKey, cachedData] of matchingQueries) {
    const nextData = computeSubQuestionResponseClearPatch(
      cachedData,
      questionId,
      patch,
      now,
    );
    if (!nextData) continue;
    snapshots.push({ queryKey, data: cachedData });
    queryClient.setQueryData(queryKey, nextData);
  }
  return snapshots;
}

function computeSubQuestionResponseClearPatch(
  cachedData: GetAuditSummaryCommandOutput | undefined,
  questionId: string,
  patch: SubQuestionResponseClearPatch,
  now: Date,
): GetAuditSummaryCommandOutput | null {
  if (!cachedData?.questions || !cachedData.audit) return null;
  const targetIndex = cachedData.questions.findIndex(
    (q) => q.questionDetails.questionId === questionId,
  );
  if (targetIndex < 0) return null;
  const targetQuestion = cachedData.questions.at(targetIndex);
  if (!targetQuestion) return null;

  const previousMainResponse = targetQuestion.responseDetails?.mainQuestionResponse;
  const previousMainResult = previousMainResponse?.responseResult;
  const previousSubResponses = targetQuestion.responseDetails?.subQuestionResponses ?? [];

  // Nothing cached for the target — nothing to clear. The draft's
  // own guard (previousResult === undefined) makes this unreachable
  // from the UI; defensive for cache drift across windows.
  if (
    !previousSubResponses.some((entry) => entry.subQuestionId === patch.subQuestionId)
  ) {
    return null;
  }

  const nextSubResponses = previousSubResponses.map((entry) =>
    entry.subQuestionId === patch.subQuestionId
      ? {
          ...entry,
          response: clearResponseForCache(entry.response, patch.respondedBy, now),
        }
      : entry,
  );

  const previousCompleted = deriveParentCompletion(
    previousSubResponses,
    patch.subQuestionRefs,
  );
  const derivedNextMain = deriveParentResult(nextSubResponses, patch.subQuestionRefs);
  const nextCompleted = deriveParentCompletion(nextSubResponses, patch.subQuestionRefs);
  const anySubAnswered = nextSubResponses.some((entry) =>
    isSubEntryAnswered(entry, entry.responseType),
  );

  // Status reflects whether any child remains answered. Result is
  // re-derived separately because an answered FAIL sibling keeps the
  // fail-dominant aggregate even though the parent is incomplete.
  const nextMainResponse = {
    ...(previousMainResponse ?? EMPTY_RESPONSE),
    responseStatus: anySubAnswered
      ? AuditQuestionCompletionStatus.IN_PROGRESS
      : AuditQuestionCompletionStatus.NOT_STARTED,
    responseResult: derivedNextMain,
    respondedBy: patch.respondedBy,
    respondedAt: now,
  } as unknown as QuestionResponseDetails;
  if (derivedNextMain === undefined) {
    delete (nextMainResponse as Partial<QuestionResponseDetails>).responseResult;
  }

  return buildNextCacheData(
    cachedData,
    targetIndex,
    nextMainResponse,
    nextSubResponses,
    applyMetricsDiff(
      cachedData.audit.metrics,
      previousMainResult,
      derivedNextMain,
      previousCompleted,
      nextCompleted,
    ),
  );
}

/**
 * Rebuild a cached response with its answer withdrawn: result +
 * naReason removed, status NOT_STARTED, responder stamped. Comment
 * / mediaIds / taskIds ride through the spread — cache/wire parity
 * with the clear builders' preservation contract.
 */
function clearResponseForCache(
  previous: QuestionResponseDetails | undefined,
  respondedBy: string,
  respondedAt: Date,
): QuestionResponseDetails {
  const next = {
    ...(previous ?? EMPTY_RESPONSE),
    responseStatus: AuditQuestionCompletionStatus.NOT_STARTED,
    respondedBy,
    respondedAt,
  } as unknown as QuestionResponseDetails;
  delete (next as Partial<QuestionResponseDetails>).responseResult;
  delete (next as Partial<QuestionResponseDetails>).naReason;
  return next;
}
